import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import {
  AbsoluteVerticalPlane,
  AnalysisSettings,
  AtticSettings,
  BasicProfile,
  BeamPocketLayoutFeatureArea,
  BearingEnvelope,
  BearingEnvelopeType,
  CeilingPlane,
  CeilingTray,
  ChaseLayoutFeatureArea,
  ComponentDefinitionType,
  ComponentDesign,
  ComponentTemplateType,
  ComponentType,
  DEFAULT_ORDERED_CUTS,
  DesignStandard,
  Direction2D,
  FeaturePatternTrussContainer,
  FeaturePatternTrussContainerSlopeSettings,
  FeaturePatternTrussSettings,
  GableSettings,
  HeelSettings,
  JackGirderConnection,
  JackInformation,
  LayoutFeatureArea,
  LoadSettings,
  MaterialSelection,
  MaterialSettings,
  MaterialSettingsPair,
  MemberAnalysisInfo,
  NewTrussGroup,
  OrganizationSettings,
  OverhangSettings,
  PiggybackSettings,
  PlateOption,
  PlatePlacement,
  PlateType,
  Point2D,
  ProfileDefaults,
  ProfileTrussSettings,
  RoofPlane,
  StepLayoutFeatureArea,
  TopOrBottomCut,
  TrussEnvelope,
  VerticalStackLayoutFeatureArea,
  WebbingInfo,
  WorkLineScope,
} from '@paragon/contract-library';
import {
  CeilingTrayGeometry,
  InsertionCeilingPlane,
  InsertionRoofPlane,
  isRoofPlane,
  NonGeometricInsertionCeilingPlane,
  NonGeometricInsertionRoofPlane,
} from '@paragon/layout';
import { assertExhaustiveSwitch, MappedOmit, ModifierKeys, PartialPartial } from '@paragon/utilities';

import { PlateDimensions } from '../../components/interface/subpanels/properties/PlatePairProperties';
import { calculateLayoutFeatureArea2DShape } from '../../library/layout-geometry/layoutFeatureAreas';
import { TrussEnvelopePersistedDefaultProperties } from '../../library/layout-geometry/truss-envelopes/types';
import {
  getDefaultInsertionWorkLine,
  type InsertionWorkLine,
} from '../../library/layout-geometry/work-lines/insertionWorkLine';
import { SerializedEntitySnapTarget } from '../../library/layout-snapping/EntitySnapTarget';
import type { MousePositionSnapType } from '../../library/layout-snapping/point-snapping/snapMousePosition';
import { SelectedItemIdentifier } from '../../library/selection/types';
import { IPartialTransformation } from '../../library/transformations/types';
import { commitBearing, commitMembers } from './componentDesignsSlice';
import { INITIAL_CEILING_PLANE_PROPERTIES, INITIAL_ROOF_PLANE_PROPERTIES, layoutSlice } from './layoutSlice';
import { setMatchFasciaHeightsPanelOpen } from './panelDataSlice';
import {
  __UNSAFE_designItemsSelected,
  __UNSAFE_itemsDeselected,
  __UNSAFE_layoutItemsSelected,
  clearSelection,
} from './selectionsSlice';
import { loadStation } from './stationsSlice';

interface InsertingReferenceLineInfo {
  readonly type: 'referenceLine';
  readonly basicDefinition: BasicLineDefinition;
}

export const isInsertingReferenceLine = (
  insertingObjectInfo: IInsertingObjectInfo,
): insertingObjectInfo is InsertingReferenceLineInfo => insertingObjectInfo?.type === 'referenceLine';

export type InsertingBearingInfo = {
  type: 'bearing';
};
export const isInsertingBearing = (
  insertingObjectInfo: IInsertingObjectInfo,
): insertingObjectInfo is InsertingBearingInfo => insertingObjectInfo?.type === 'bearing';

export type InsertingPlatePairInfo = {
  type: 'platePair';

  position?: Point2D;
  memberGuid?: string; // Helpful for top/bottom plates

  plateType?: PlateType;
  readonly tangiblePlateTypesForComboPlateType?: readonly PlateType[];
  plateDimensions?: PlateDimensions;
  platePlacement: PlatePlacement;
};

type InsertingSpliceInfo = {
  readonly type: 'splice';
  readonly memberGuid: string | null;
};

type InsertingLoadInfo = {
  type: 'load';
};

type InsertingJackGirderConnectionInfo = {
  type: 'jackGirderConnection';
};

type InsertingParapetLoadSettingsInfo = {
  type: 'parapetLoadSettings';
};

type InsertingMovingLoadSettingsInfo = {
  type: 'movingLoadSettings';
};

type InsertingLoadCaseInfo = {
  type: 'loadCase';
  loadInfo: { isPresent: true; loadGuid: string } | { isPresent: false };
};

export type InsertingTrussEnvelopeData = PartialPartial<
  Omit<TrussEnvelope, 'name'>,
  'leftPoint' | 'rightPoint' | 'elevation' | 'leftOverhang' | 'rightOverhang'
>;

export type InsertingTrussEnvelopeInfo = {
  type: 'trussEnvelope';
  data: InsertingTrussEnvelopeData;
};

export type InsertingTrussEnvelopeRunInfo = {
  type: 'trussEnvelopeRun';
  data: {
    baseEnvelopeGuid: string;

    spacing: number;
    calculateEnvelopes: boolean;
  };
};

export type InsertingTrussGroupInfo = {
  type: 'trussGroup';
  data: NewTrussGroup;
};

export type CornerSetType = 'Stepdown' | 'Atlantic';

export type InsertingCornerSetInfo = {
  type: 'cornerSet';
  data: {
    type: CornerSetType;
    spacing: number;
    plane1Guid?: string;
    plane2Guid?: string;
    girderGuid: string;
    minJackLength: number;
  };
};

export type WorkLineInsertionSelection =
  | { readonly type: 'point'; readonly point: Point2D }
  | { readonly type: 'line'; readonly base: SerializedEntitySnapTarget };

export interface WorkLineInsertionSettings {
  readonly bounding: boolean;
  readonly segmentDesired: boolean; // signifies that the user wishes a point-to-point line to be a segment
  readonly autoInsert: boolean; // when true the second click commits immediately; otherwise it locks for review
  readonly divisionQuantity: number; // default quantity for newly inferred or manually selected division lines
}

export type WorkLineInsertionDraft =
  | { readonly phase: 'idle' }
  | { readonly phase: 'gesturing'; readonly firstSelection: WorkLineInsertionSelection }
  | { readonly phase: 'reviewing'; readonly line: InsertionWorkLine };

export type InsertingWorkLineInfo = {
  readonly type: 'workLine';

  // The actual gesture line depends on in-scene mouse position. This mirror is only for convenient reference if needed.
  readonly liveGestureLineType: InsertionWorkLine['type'] | null;

  readonly draft: WorkLineInsertionDraft;
  readonly settings: WorkLineInsertionSettings;
  readonly scope: WorkLineScope;
};

export type InsertionBearingSettings = Omit<BearingEnvelope, 'guid' | 'name' | 'leftPoint' | 'rightPoint'>;

export const DEFAULT_BEARING_ENVELOPE_HEIGHT = 8 * 12;

export const DEFAULT_WALL_DEPTH = { top: DEFAULT_BEARING_ENVELOPE_HEIGHT, bottom: 0 };
export const DEFAULT_BEAM_DEPTH = {
  top: DEFAULT_BEARING_ENVELOPE_HEIGHT,
  bottom: DEFAULT_BEARING_ENVELOPE_HEIGHT - 11.25,
};

export const DEFAULT_BEARING_ENVELOPE_SETTINGS: Omit<InsertionBearingSettings, 'top' | 'bottom'> = {
  thickness: 5.5, // ensure this remains consistent with the default `BearingWidth` for `ProfileDefaults`
  numberOfPlies: 1,
  bearingType: 'Wall',
  beamOrientation: 'Drop',
  justification: 'Front',
  nonStructural: false,
  levelGuid: null,
};

export type InsertionBearingEnvelope = InsertionBearingSettings &
  Partial<Pick<BearingEnvelope, 'leftPoint' | 'rightPoint'>>;

type InsertingBearingEnvelopeInfo = {
  type: 'bearingEnvelope';
  data: InsertionBearingEnvelope;
};

export function isInsertionRoofPlaneFullyDefined(plane: InsertionRoofPlane): plane is RoofPlane {
  return plane.geometry != null;
}

type InsertingRoofPlaneInfo = {
  type: 'roofPlane';
  data: InsertionRoofPlane;
};

export function isInsertionCeilingPlaneFullyDefined(plane: InsertionCeilingPlane): plane is CeilingPlane {
  return plane.geometry != null;
}

export function isInsertionPlaneFullyDefined(plane: InsertionRoofPlane | InsertionCeilingPlane): boolean {
  return isRoofPlane(plane) ? isInsertionRoofPlaneFullyDefined(plane) : isInsertionCeilingPlaneFullyDefined(plane);
}

type InsertingCeilingPlaneInfo = {
  type: 'ceilingPlane';
  data: InsertionCeilingPlane;
};

export type InsertionCeilingTray = PartialPartial<CeilingTray, 'guid' | 'position'>;

const INITIAL_CEILING_TRAY_PROPERTIES = {
  verticalAnchor: 'Center',
  horizontalAnchor: 'Center',

  width: 0,
  length: 0,
  rotationAngle: 0,

  bottomElevation: 0,
  topElevation: 0,

  sideAngle: 90,

  levelGuid: null,
} satisfies InsertionCeilingTray;

export function isInsertionCeilingTrayFullyDefined(tray: InsertionCeilingTray): tray is Omit<CeilingTray, 'guid'> {
  if (
    tray.position == null ||
    tray.topElevation == null ||
    tray.bottomElevation == null ||
    tray.width == null ||
    tray.length == null
  ) {
    return false;
  }

  if (CeilingTrayGeometry.fromTray(tray as Omit<CeilingTray, 'guid'>) == null) {
    return false;
  }

  return true;
}

type IInsertingCeilingTrayInfo = {
  type: 'ceilingTray';
  data: InsertionCeilingTray;
};

export type InsertionFeaturePatternContainer = Omit<FeaturePatternTrussContainer, 'guid'> & {
  slopeSettings: PartialPartial<FeaturePatternTrussContainerSlopeSettings, 'edgeToSlopeFrom'> | null;
};

type IInsertingFeaturePatternContainerInfo = {
  type: 'featurePatternContainer';
  data: InsertionFeaturePatternContainer;
};

export const FEATURE_AREA_UNDEFINED_POSITION = Object.freeze({ x: 0, y: 0 });

const ZERO_FEATURE_AREA_AREA = Object.freeze({
  type: 'Rectangle2D' as const,
  position: FEATURE_AREA_UNDEFINED_POSITION,
  verticalAnchor: 'Center' as const,
  horizontalAnchor: 'Center' as const,
  width: 0,
  length: 0,
  rotationAngle: 0,
});

const DEFAULT_CHASE_FEATURE = Object.freeze({
  featureType: 'Chase' as const,
  area: ZERO_FEATURE_AREA_AREA,
  minWidth: null,
  maxWidth: null,
}) satisfies Omit<ChaseLayoutFeatureArea, 'guid'>;

const DEFAULT_STEP_FEATURE = Object.freeze({
  featureType: 'Step' as const,
  area: ZERO_FEATURE_AREA_AREA,
  height: 0,
}) satisfies Omit<StepLayoutFeatureArea, 'guid'>;

const DEFAULT_BEAM_POCKET_FEATURE = Object.freeze({
  featureType: 'BeamPocket' as const,
  area: ZERO_FEATURE_AREA_AREA,
  beamWidth: null,
  pocketHeight: 0,
  bottomChordCutOut: false,
  leftVerticalsCount: 1,
  rightVerticalsCount: 1,
}) satisfies Omit<BeamPocketLayoutFeatureArea, 'guid'>;

const DEFAULT_VERTICAL_STACK_FEATURE = Object.freeze({
  featureType: 'VerticalStack' as const,
  area: ZERO_FEATURE_AREA_AREA,

  bearingWidth: null,
  verticalsCount: 0,
}) satisfies Omit<VerticalStackLayoutFeatureArea, 'guid'>;

export type InsertionLayoutFeatureArea = MappedOmit<LayoutFeatureArea, 'guid'>;

type IInsertingFeatureAreaInfo = {
  type: 'featureArea';
  data: {
    featurePatternContainerGuid: string;
    value: InsertionLayoutFeatureArea;
  };
};

export function isInsertionFeatureAreaFullyDefined(area: InsertionLayoutFeatureArea): boolean {
  return calculateLayoutFeatureArea2DShape(area) != null;
}

export interface ReferenceLineIntersection {
  readonly line1Guid: string;
  readonly line2Guid: string;
  readonly point: Point2D;
}

export function ensureIntersectionIsSerializable(intersection: ReferenceLineIntersection) {
  return { ...intersection, point: { x: intersection.point.x, y: intersection.point.y } };
}

export type IntersectionSelection = { type: 'intersection'; value: ReferenceLineIntersection };
export type LineSelection = { type: 'line'; value: string };
export type DirectionSelection = { type: 'direction'; value: Direction2D };
export type OffsetSelection = { type: 'offset'; value: number };

export type BasicInsertionFirstSelection = IntersectionSelection | LineSelection;
export type BasicInsertionSecondSelection =
  | IntersectionSelection // intersection + intersection => point-to-point line; OR line + intersection => perpendicular line
  | LineSelection //  line + line => division line
  | DirectionSelection // intersection + direction => point-slope line
  | OffsetSelection; // line + offset => parallel line

export interface BasicInsertionSettings {
  /** The number of lines that the user wishes to insert. Not supported by all basic insertion modes. */
  plurality: number;
}

const DEFAULT_BASIC_INSERTION_SETTINGS = Object.freeze({ repeat: false, plurality: 1 });

export interface BasicLineDefinition {
  readonly settings: BasicInsertionSettings;
  readonly firstSelection?: BasicInsertionFirstSelection;
}

export type SnappingLevel = 'coarse' | 'default' | 'fine';

export interface InsertingMemberInfo {
  readonly type: 'member';
  readonly isBrace: boolean;
  readonly basicDefinition: BasicLineDefinition;
}

interface InsertingGussetInfo {
  readonly type: 'gusset';
  readonly centerPoint: Point2D | null;
}

export const isInsertingMember = (
  insertingObjectInfo: IInsertingObjectInfo,
): insertingObjectInfo is InsertingMemberInfo => insertingObjectInfo?.type === 'member';

interface InsertingMemberStackInfo {
  type: 'memberStack';
  memberGuid: string;
}

interface InsertingDroppedTopChordsInfo {
  type: 'droppedTopChords';
  thickness: number;
  angle: number;
  fullyCrossing: boolean;
}

export type ComponentInsertionShape =
  | 'FreeformProfile'
  | 'FeaturePattern'
  | 'Attic'
  | 'Common'
  | 'Gable'
  | 'Jack'
  | 'Scissors'
  | 'Valley'
  | 'Piggyback'
  | 'G2T-TC-TC'
  | 'G2T-BC-BC'
  | 'G2T-BC-TC'
  | 'G2T-TC-BC'
  | 'PFT'
  | 'PFT-J';

/**
 * The shapes that have settings that differ from the normal design settings.
 */
export type ConfigurableShapes =
  'Attic' | 'Common' | 'FreeformProfile' | 'Gable' | 'Jack' | 'Scissors' | 'Valley' | 'Piggyback';

interface ShapeDetailsBase {
  readonly type: ConfigurableShapes;
}

export interface FreeformProfileShapeDetails extends ShapeDetailsBase {
  readonly type: 'FreeformProfile';
  readonly span: number;
}

export interface AtticShapeDetails extends ShapeDetailsBase {
  readonly type: 'Attic';
  readonly span: number;
  readonly slopeInDegrees: number;
  readonly heelSettings: HeelSettings;
  readonly overhang: OverhangSettings;
  readonly atticSettings: AtticSettings;
}

export interface CommonShapeDetails extends ShapeDetailsBase {
  readonly type: 'Common';
  readonly span: number;
  readonly slopeInDegrees: number;
  readonly heelSettings: HeelSettings;
  readonly overhang: OverhangSettings;
}

export interface GableShapeDetails extends ShapeDetailsBase {
  readonly type: 'Gable';
  readonly span: number;
  readonly slopeInDegrees: number;
  readonly heelSettings: HeelSettings;
  readonly overhang: OverhangSettings;
  readonly gableSettings: GableSettings;
}

export interface JackShapeDetails extends ShapeDetailsBase {
  readonly type: 'Jack';
  readonly span: number;
  readonly slopeInDegrees: number;
  readonly leftHeelSettings: HeelSettings;
  readonly leftOverhang: OverhangSettings;
}

export interface ScissorsShapeDetails extends ShapeDetailsBase {
  readonly type: 'Scissors';
  readonly span: number;
  readonly topChordSlopeInDegrees: number;
  readonly bottomChordSlopeInDegrees: number;
  readonly heelSettings: HeelSettings;
  readonly overhang: OverhangSettings;
  readonly seatCut: number;
}

export interface ValleyShapeDetails extends ShapeDetailsBase {
  readonly type: 'Valley';
  readonly span: number;
  readonly slopeInDegrees: number;
}

export interface PiggybackShapeDetails extends ShapeDetailsBase {
  readonly type: 'Piggyback';
  readonly span: number;
  readonly slopeInDegrees: number;
  readonly piggybackSettings: PiggybackSettings;
}

export type ConfigurableShapeDetails =
  | AtticShapeDetails
  | CommonShapeDetails
  | FreeformProfileShapeDetails
  | GableShapeDetails
  | JackShapeDetails
  | PiggybackShapeDetails
  | ScissorsShapeDetails
  | ValleyShapeDetails;

export const componentInsertionStages = ['ChoosingShape', 'ShapeDetails', 'StructuralDetails'] as const;

export type ComponentInsertionStage = (typeof componentInsertionStages)[number];

/**
 * Defines whether the settings are the initial settings or if they've been modified. If we're in a project, and they
 * haven't been modified, we don't want to save them, because they should inherit from their parent instead.
 */
type SettingsState = 'initial' | 'modified';

export interface InsertingComponentDesignInfo {
  readonly type: 'componentDesign';
  readonly parentGuid: string | null;

  readonly oldComponentDesign: ComponentDesign | null;
  readonly previouslySelectedComponentDesignGuid: string | null;

  readonly loadingInfo: { readonly isLoading: true; readonly downloadId: {} } | { readonly isLoading: false };
  readonly loadedInfo: { readonly isLoaded: true; readonly guid: string } | { readonly isLoaded: false };

  readonly componentType: ComponentType;
  readonly definitionType: ComponentDefinitionType;
  readonly templateType: ComponentTemplateType;
  readonly shape: ComponentInsertionShape;
  readonly stage: ComponentInsertionStage;

  readonly shapeDetails: ConfigurableShapeDetails | null;

  readonly profileDefaults: ProfileDefaults | null;
  readonly designSettings: ProfileTrussSettings | FeaturePatternTrussSettings;

  readonly materialSelection: MaterialSelection;

  readonly loadSettings: LoadSettings;
  readonly analysisSettings: AnalysisSettings;
  readonly materialSettings: MaterialSettingsPair;

  readonly loadSettingsState: SettingsState;
  readonly analysisSettingsState: SettingsState;
  readonly materialSettingsState: SettingsState;

  readonly jackConnections: readonly JackGirderConnection[];
  readonly jackInformation: { readonly [componentDesignGuid: string]: JackInformation };

  readonly webbingInfo?: WebbingInfo | null;
  readonly memberAnalysisInfo?: MemberAnalysisInfo | null;

  readonly insertingBearing?: boolean;
}

type MemberCutTargetInfo = {
  type: 'memberCut';
  relativeGeometryGuid: string;
  isForBaseCut: boolean;
  replacingLineGuid: string | undefined;
};

type RegularBearingLineTargetInfo = {
  type: 'bearingLine';
  bearingGeometryType: 'Bearing';
  propertyName: 'bearingLineGuid' | 'lineGuid';
  bearingGuid: string;
};

type ContinuousBearingLineTargetInfo = {
  type: 'bearingLine';
  bearingGeometryType: 'ContinuousBearing';
  propertyName: 'bearingLineGuid' | 'leftSideLineGuid' | 'rightSideLineGuid';
  bearingGuid: string;
};

type BearingLineTargetInfo = RegularBearingLineTargetInfo | ContinuousBearingLineTargetInfo;

type PickingReferenceLineInfo = {
  type: 'pickingReferenceLine';
  targetInfo:
    | { type: 'referenceLine'; rootGuid: string; key: string }
    | { type: 'memberLine'; memberGuid: string }
    | MemberCutTargetInfo
    | BearingLineTargetInfo;
};
export type PickingReferenceLineIntersectionInfo = {
  type: 'pickingReferenceLineIntersection';
  referenceLineInfo: { lineGuidKey1: keyof any; lineGuidKey2: keyof any; rootGuid: string };
};

type PickingBraceLocation = {
  type: 'pickingBraceLocation';
};

type PickingMemberInfo = PickingBraceLocation;

type PickingGussetLocation = {
  type: 'pickingGussetLocation';
};

type PickingProfileJointInfo = {
  type: 'pickingProfileJoint';
};

export type NonInsertionTrussEnvelopeTargetInfo =
  | { target: 'existingTrussEnvelope'; trussEnvelopeGuid: string }
  | { target: 'existingTrussEnvelopes'; trussEnvelopeGuids: string[] };
export type TrussEnvelopeTargetInfo = { target: 'insertionTrussEnvelope' } | NonInsertionTrussEnvelopeTargetInfo;

interface PickingRoofFaceInfo {
  readonly type: 'pickingRoofFace';
  readonly ordered: boolean;
  readonly cutOrientation: 'top' | 'bottom';
  readonly targetInfo: TrussEnvelopeTargetInfo;
}

export const STANDARD_PLANE_PICKING_MODE = 'real';

export interface PickingPlaneInfo {
  readonly type: 'pickingPlane';

  readonly targetInfo: PickingPlaneTargetInfo;

  readonly activeMode: 'real' | 'absoluteVertical';

  readonly hoveredPlane: HoveredPlaneDataWithType | null;
}

const INITIAL_PLANE_PICKING_INFO = {
  type: 'pickingPlane',
  activeMode: STANDARD_PLANE_PICKING_MODE,
  hoveredPlane: null,
} satisfies Partial<PickingPlaneInfo>;

export type HoveredPlaneDataWithType =
  | {
      readonly type: 'roofPlane' | 'ceilingPlane';
      readonly guid: string;
    }
  | { readonly type: 'absoluteVertical'; readonly geometry: AbsoluteVerticalPlane };

// Plane visibility when creating top/bottom cuts on truss envelopes is treated as a special case. The user might
//   want to pick either roof or ceiling planes in this case, but they need to be able to toggle the visibility of
//   either during picking. Thus, neither are "forced visible" via `areRoofPlanesForcedVisible` like during the
//   other plane picking modes.
const TOP_OR_BOTTOM_CUT_PICKING_MODES = [
  'truss-envelope-top-cut',
  'truss-envelope-bottom-cut',
  'truss-envelope-ordered-top-cut',
  'truss-envelope-ordered-bottom-cut',
] as const;

type TopOrBottomCutPickingMode = (typeof TOP_OR_BOTTOM_CUT_PICKING_MODES)[number];

export function isTopOrBottomCutPickingMode(mode: PickingPlaneTargetInfo['type']): mode is TopOrBottomCutPickingMode {
  return TOP_OR_BOTTOM_CUT_PICKING_MODES.includes(mode as TopOrBottomCutPickingMode);
}

export type PickingPlaneTargetInfo =
  | {
      readonly type: 'roof-cut' | 'ceiling-cut';
      readonly planeGuidToCut: string;
    }
  | {
      readonly type: (typeof TOP_OR_BOTTOM_CUT_PICKING_MODES)[number];
      readonly trussEnvelopeTargetInfo: TrussEnvelopeTargetInfo;
      readonly hasRoofPlanes: boolean;
      readonly hasCeilingPlanes: boolean;
    }
  | { readonly type: 'fascia-height-reference' }
  | { readonly type: 'corner-set-plane-1' | 'corner-set-plane-2' };

type TopOrBottomCutPickingTargetInfo = Extract<PickingPlaneTargetInfo, { type: TopOrBottomCutPickingMode }>;

export function isTopOrBottomCutPickingTargetInfo(
  targetInfo: PickingPlaneTargetInfo,
): targetInfo is TopOrBottomCutPickingTargetInfo {
  return isTopOrBottomCutPickingMode(targetInfo.type);
}

export type LayoutLinePickingTarget =
  | 'envelope-trim-or-extend-line'
  | 'parallel-snapping-base-line'
  | 'roof-plane-definition'
  | 'ceiling-plane-definition'
  | 'feature-pattern-container-boundary'
  | 'feature-pattern-container-slope-base-edge'
  | 'transformation-center'
  | 'insertion-work-line-parallel-base-line'
  | 'insertion-work-line-perpendicular-base-line'
  | 'insertion-work-line-division-line-1'
  | 'insertion-work-line-division-line-2'
  | 'insertion-work-line-exact-base-line';

interface PickingLayoutLine {
  readonly type: 'pickingLayoutLine';
  readonly target: LayoutLinePickingTarget;

  /** keep picking after a selection is made; defaults to false, and not respected by most modes */
  readonly repeat?: boolean;
}

export type LayoutPointPickingTarget =
  | 'insertion-envelope-left'
  | 'insertion-envelope-right'
  | 'editing-envelope-left'
  | 'editing-envelope-right'
  | 'bearing-envelope-left'
  | 'bearing-envelope-right'
  | 'ceiling-tray-position'
  | 'feature-position'
  | 'transformation-origin'
  | 'transformation-destination'
  | 'transformation-center'
  | 'uploaded-lines-origin'
  | 'uploaded-lines-scale-base'
  | 'uploaded-lines-scale-end'
  | 'insertion-work-line-point-angle-base'
  | 'insertion-work-line-point-to-point-base'
  | 'insertion-work-line-point-to-point-end'
  | 'insertion-work-line-perpendicular-point';

interface PickingLayoutPoint {
  readonly type: 'pickingLayoutPoint';

  /**
   * Informs what will happen once the point is picked.
   */
  readonly target: LayoutPointPickingTarget;
}

/**
 * What a pending picked point (see `PendingPickedPointMovement`) will be applied to once it has been moved.
 */
export type PickedPointMovementRecipient =
  | { readonly type: 'layoutPointPicking'; readonly target: LayoutPointPickingTarget }
  | { readonly type: 'workLineFirstSelection' };

/**
 * A point that has been picked but not yet applied, because the user requested to move it (by a distance in a
 *   direction) first. Whatever picked it (eg. layout point picking) remains active until the movement is finished.
 */
export interface PendingPickedPointMovement {
  readonly point: Point2D;
  readonly snapType: MousePositionSnapType | null;
  readonly modifiers: ModifierKeys;
  readonly recipient: PickedPointMovementRecipient;

  readonly clientPosition: Point2D; // used to position the movement popper
}

interface PickingTrussEnvelopesToAddToTrussGroup {
  readonly type: 'pickingTrussEnvelopesForTrussGroup';
  readonly trussGroupGuid: string | null; // In the null case, we're inserting a NewTrussGroup
  readonly trussEnvelopeGuidsToAdd: readonly string[];
  readonly trussEnvelopeGuidsToRemove: readonly string[];
}

export type IInsertingObjectInfo =
  | null
  | InsertingMemberInfo
  | InsertingGussetInfo
  | InsertingMemberStackInfo
  | InsertingDroppedTopChordsInfo
  | InsertingReferenceLineInfo
  | InsertingBearingInfo
  | InsertingPlatePairInfo
  | InsertingComponentDesignInfo
  | InsertingSpliceInfo
  | InsertingLoadInfo
  | InsertingJackGirderConnectionInfo
  | InsertingParapetLoadSettingsInfo
  | InsertingMovingLoadSettingsInfo
  | InsertingLoadCaseInfo
  | InsertingTrussEnvelopeInfo
  | InsertingTrussEnvelopeRunInfo
  | InsertingTrussGroupInfo
  | InsertingCornerSetInfo
  | InsertingWorkLineInfo
  | InsertingBearingEnvelopeInfo
  | InsertingRoofPlaneInfo
  | InsertingCeilingPlaneInfo
  | IInsertingCeilingTrayInfo
  | IInsertingFeaturePatternContainerInfo
  | IInsertingFeatureAreaInfo;

export type IPickingObjectInfo =
  | null
  | PickingReferenceLineInfo
  | PickingReferenceLineIntersectionInfo
  | PickingMemberInfo
  | PickingGussetLocation
  | PickingProfileJointInfo
  | PickingLayoutLine
  | PickingLayoutPoint
  | PickingRoofFaceInfo
  | PickingPlaneInfo
  | PickingTrussEnvelopesToAddToTrussGroup;

export const multiPickTypes: readonly Exclude<IPickingObjectInfo, null>['type'][] = [
  'pickingTrussEnvelopesForTrussGroup',
];

type IImportingComponentInfo = null | {
  parentGuid?: string;
  templateComponentGuid?: string;
};

export type ComponentSortType = 'name' | 'capacity';

export const ANALYSIS_STATUS_FILTER_VALUES = ['off', 'analyzed', 'unanalyzed'] as const;
export type AnalysisStatusFilter = (typeof ANALYSIS_STATUS_FILTER_VALUES)[number];

export const CHECK_STATUS_FILTER_VALUES = ['off', 'passing', 'failing'] as const;
export type CheckStatusFilter = (typeof CHECK_STATUS_FILTER_VALUES)[number];

export const LEVEL_FILTER_VALUES = ['off', 'visibleLevels', 'hiddenLevels'] as const;
export type LevelFilter = (typeof LEVEL_FILTER_VALUES)[number];

export const LINKAGE_FILTER_VALUES = ['off', 'linked', 'unlinked'] as const;
export type LinkageFilter = (typeof LINKAGE_FILTER_VALUES)[number];

export interface ComponentListFilters {
  readonly analysisStatus: AnalysisStatusFilter;
  readonly checkStatus: CheckStatusFilter;
  readonly level: LevelFilter;
  readonly linkage: LinkageFilter;
}

export const DEFAULT_COMPONENT_LIST_FILTERS: ComponentListFilters = {
  analysisStatus: 'off',
  checkStatus: 'off',
  level: 'visibleLevels',
  linkage: 'off',
};

/**
 * Defines a top-level item that is open in the design client.  Used at startup to communicate what needs to be loaded,
 *   and by the navigation panel to determine what top-level rows to render.
 */
export type ClientContext =
  | { type: 'job'; jobId: number }
  | { type: 'project'; projectGuid: string }
  | { type: 'station'; stationGuid: string }
  | { type: 'schedule-station'; stationGuid: string }
  | { type: 'shared-component-set'; sharedComponentSetLookupCode: string };

export interface RewebbingProfileSettings {
  readonly profile: BasicProfile;
  readonly gableSettings: GableSettings;
}

interface RewebbingInfo {
  readonly profileSettings: RewebbingProfileSettings;
  readonly webbingInfo: WebbingInfo | null;
  readonly memberAnalysisInfo: MemberAnalysisInfo | null;

  // never mutated, stored here so that we can provide them to the rewebbing route without fetching them each time
  readonly materialSettings: MaterialSettings;
  readonly loadSettings: LoadSettings;
  readonly analysisSettings: AnalysisSettings;
  readonly jackConnections: readonly JackGirderConnection[];
  readonly jackInformation: { readonly [componentDesignGuid: string]: JackInformation };
}

type ITransformingObjectsInfo = IPartialTransformation | null;

export interface Application {
  readonly inDimensionMode: boolean;
  readonly inMemberCuttingMode: false | 'transitive' | 'sticky';
  readonly openContext: ClientContext | null;
  readonly schemaGuid: string | null;
  readonly insertingObjectInfo: IInsertingObjectInfo;
  readonly snappingLevel: SnappingLevel;

  /**
   * A `IPickingObjectInfo` that can be manually set to (temporarily) turn on a specific picking mode. Never depend on
   *   this property directly to determine whether picking is active. See the `getPickingObjectInfo` selector instead.
   */
  readonly manualPickingObjectInfo: IPickingObjectInfo;

  readonly pendingPickedPointMovement: PendingPickedPointMovement | null;

  readonly rewebbingInfo: RewebbingInfo | null;
  /**
   * Sometimes it is necessary to know not just what reference line was selected, but also where on the line the user
   * selected. This can help us infer intent while defining the member, particularly in determining the intended
   * position.
   */
  readonly lastMemberLineSelectionPoint?: Point2D;
  readonly importingComponentInfo: IImportingComponentInfo;
  readonly componentDesignSortBy: ComponentSortType | string[];
  readonly componentListFilters: ComponentListFilters;
  readonly customerGuid?: string;
  readonly organizationGuid?: string;
  readonly detailedAnalysis: boolean;
  readonly regenerateTddsAutomatically: boolean;
  readonly runPlateMislocations: boolean;
  readonly runExhaustivePlateSearch: boolean;
  readonly runOptimizations: boolean;
  readonly allowOptimizationChordResizing: boolean;
  readonly transformingObjectsInfo: ITransformingObjectsInfo | null;
  readonly organizationSettings: OrganizationSettings | null;
  /** Distinguishes settings we have not fetched yet from an organization that has none. */
  readonly organizationSettingsLoaded: boolean;
}

const initialState: Application = {
  inDimensionMode: false,
  inMemberCuttingMode: false,
  openContext: null,
  schemaGuid: null,
  insertingObjectInfo: null,
  snappingLevel: 'default',
  manualPickingObjectInfo: null,
  pendingPickedPointMovement: null,
  rewebbingInfo: null,
  importingComponentInfo: null,
  componentDesignSortBy: 'name',
  componentListFilters: DEFAULT_COMPONENT_LIST_FILTERS,
  detailedAnalysis: false,
  regenerateTddsAutomatically: true,
  runPlateMislocations: true,
  runExhaustivePlateSearch: false,
  runOptimizations: false,
  allowOptimizationChordResizing: false,
  transformingObjectsInfo: null,
  organizationSettings: null,
  organizationSettingsLoaded: false,
};

function designSettingsHaveAtticSpace(designSettings: Draft<ProfileTrussSettings | FeaturePatternTrussSettings>) {
  return designSettings.definitionType === 'Profile' && designSettings.profile.atticSettings != null;
}

function synchronizeAtticLoadWithAtticSpace(
  insertingObjectInfo: Draft<InsertingComponentDesignInfo>,
  hadAtticSpace: boolean,
) {
  const hasAtticSpace = designSettingsHaveAtticSpace(insertingObjectInfo.designSettings);
  if (hasAtticSpace === hadAtticSpace) return;

  if (hasAtticSpace === insertingObjectInfo.loadSettings.considerAtticLoad) return;

  insertingObjectInfo.loadSettings.considerAtticLoad = hasAtticSpace;
  insertingObjectInfo.loadSettingsState = 'modified';
}

const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    startup: (
      state,
      action: PayloadAction<{
        context: ClientContext | undefined;

        activeComponentDesignGuid: string | null;
        selectedItem: SelectedItemIdentifier | null;
        selectedLoadCaseGuid: string | null;
        selectedAnalysisSetGuid: string | null;
      }>,
    ) => {
      if (action.payload.context) {
        state.openContext = action.payload.context;
      }
    },
    setDimensionMode: (state, action: PayloadAction<boolean>) => {
      state.inDimensionMode = action.payload;
    },
    toggleMemberCuttingMode: (state, action: PayloadAction<boolean | 'transitive' | 'sticky' | undefined>) => {
      switch (action.payload) {
        case true:
          state.inMemberCuttingMode = 'transitive';
          break;

        case false:
        case 'transitive':
        case 'sticky':
          state.inMemberCuttingMode = action.payload;
          break;

        case undefined:
          state.inMemberCuttingMode =
            typeof state.inMemberCuttingMode === 'string' || Boolean(state.inMemberCuttingMode) ? false : 'transitive';
      }
    },
    toggleDimensionMode: (state) => {
      state.inDimensionMode = !state.inDimensionMode;
    },
    startInsertMember: (state, action: PayloadAction<{ isBrace: boolean }>) => {
      state.insertingObjectInfo = {
        type: 'member',
        isBrace: action.payload.isBrace,
        basicDefinition: { settings: DEFAULT_BASIC_INSERTION_SETTINGS },
      };
    },
    applicationOnStartDropTopChords: (state, action: PayloadAction<{ angle: number }>) => {
      const { angle } = action.payload;
      state.insertingObjectInfo = { type: 'droppedTopChords', thickness: 1.5, angle, fullyCrossing: true };
    },
    editDropTopChordsInsertion: (state, action: PayloadAction<Partial<InsertingDroppedTopChordsInfo>>) => {
      if (state.insertingObjectInfo?.type !== 'droppedTopChords') throw new Error('Invalid state');

      state.insertingObjectInfo = { ...state.insertingObjectInfo, ...action.payload };
    },
    applicationOnInsertingMemberStack: (state, action: PayloadAction<string>) => {
      state.insertingObjectInfo = { type: 'memberStack', memberGuid: action.payload };
    },
    applicationStartInsertBearing: (state) => {
      state.insertingObjectInfo = { type: 'bearing' };
    },
    applicationOnInsertingSplice: (state, action: PayloadAction<string | null>) => {
      state.insertingObjectInfo = { type: 'splice', memberGuid: action.payload };
    },
    applicationOnInsertingManualLoad: (state) => {
      state.insertingObjectInfo = { type: 'load' };
    },
    applicationOnInsertingJackGirderConnection: (state) => {
      state.insertingObjectInfo = { type: 'jackGirderConnection' };
    },
    applicationOnInsertingMovingLoadSettings: (state) => {
      state.insertingObjectInfo = { type: 'movingLoadSettings' };
    },
    applicationOnInsertingParapetLoadSettings: (state) => {
      state.insertingObjectInfo = { type: 'parapetLoadSettings' };
    },
    applicationOnInsertingLoadCase: (state) => {
      state.insertingObjectInfo = { type: 'loadCase', loadInfo: { isPresent: false } };
    },
    applicationOnInsertingReferenceLine: (state) => {
      state.insertingObjectInfo = {
        type: 'referenceLine',
        basicDefinition: { settings: DEFAULT_BASIC_INSERTION_SETTINGS },
      };
    },
    applicationOnInsertingGusset: (state) => {
      state.insertingObjectInfo = {
        type: 'gusset',
        centerPoint: null,
      };
    },
    applicationOnSetInsertionGussetCenterPoint: (state, action: PayloadAction<Point2D | null>) => {
      state.insertingObjectInfo = {
        type: 'gusset',
        centerPoint: action.payload,
      };
    },

    applicationOnInsertingTrussEnvelope: (
      state,
      action: PayloadAction<
        Pick<
          TrussEnvelope,
          'roofContainerGuid' | 'featurePatternContainerGuid' | 'componentType' | 'definitionType' | 'levelGuid'
        > &
          TrussEnvelopePersistedDefaultProperties
      >,
    ) => {
      state.insertingObjectInfo = {
        type: 'trussEnvelope',
        data: {
          ...castDraft(action.payload),

          guid: crypto.randomUUID(),
          componentDesignGuid: null,
          topCuts: [],
          bottomCuts: [],
          orderedTopCuts: castDraft(DEFAULT_ORDERED_CUTS),
          orderedBottomCuts: castDraft(DEFAULT_ORDERED_CUTS),
          leftBevelCut: null,
          rightBevelCut: null,
          leftEndConnectionOverride: null,
          rightEndConnectionOverride: null,
          containerLevel: 'NotCapped',
          capLevelOne: 0,
          capLevelTwo: 0,
          featurePatternSettings: null,
          labelPlacement: 'Left',
        },
      };
    },

    resetInsertionTrussEnvelope: (state) => {
      if (state.insertingObjectInfo?.type === 'trussEnvelope') {
        state.insertingObjectInfo.data.guid = crypto.randomUUID();
        state.insertingObjectInfo.data.componentDesignGuid = null;

        delete state.insertingObjectInfo.data.leftPoint;
        delete state.insertingObjectInfo.data.rightPoint;

        state.insertingObjectInfo.data.leftBevelCut = null;
        state.insertingObjectInfo.data.rightBevelCut = null;
      } else {
        throw new Error('Invalid insertion state.');
      }
    },

    setInsertionTrussEnvelopeProperties: (state, action: PayloadAction<Partial<Omit<TrussEnvelope, 'guid'>>>) => {
      if (state.insertingObjectInfo?.type === 'trussEnvelope') {
        Object.assign(state.insertingObjectInfo.data, action.payload);
      } else {
        throw new Error('Invalid insertion state.');
      }
    },

    applicationOnInsertingTrussEnvelopeRun: (state, action: PayloadAction<{ baseEnvelopeGuid: string }>) => {
      state.insertingObjectInfo = {
        type: 'trussEnvelopeRun',
        data: {
          baseEnvelopeGuid: action.payload.baseEnvelopeGuid,
          spacing: 24,
          calculateEnvelopes: true,
        },
      };
    },

    setInsertionRunSpacing: (state, action: PayloadAction<number>) => {
      if (state.insertingObjectInfo?.type === 'trussEnvelopeRun') {
        state.insertingObjectInfo.data.spacing = action.payload;
      }
    },

    setInsertionRunCalculation: (state, action: PayloadAction<boolean>) => {
      if (state.insertingObjectInfo?.type === 'trussEnvelopeRun') {
        state.insertingObjectInfo.data.calculateEnvelopes = action.payload;
      }
    },

    applicationOnInsertingTrussGroup: (
      state,
      action: PayloadAction<{ readonly name: string; readonly trussEnvelopeGuids: readonly string[] }>,
    ) => {
      state.insertingObjectInfo = {
        type: 'trussGroup',
        data: {
          name: action.payload.name,
          trussEnvelopeGuids: [],
          parentGuid: null,
        },
      };

      // Enter picking mode right away
      state.inDimensionMode = false;
      state.manualPickingObjectInfo = {
        type: 'pickingTrussEnvelopesForTrussGroup',
        trussGroupGuid: null,
        trussEnvelopeGuidsToAdd: castDraft(action.payload.trussEnvelopeGuids),
        trussEnvelopeGuidsToRemove: [],
      };
    },

    updateInsertingTrussGroup: (state, action: PayloadAction<Partial<InsertingTrussGroupInfo['data']>>) => {
      if (state.insertingObjectInfo?.type !== 'trussGroup') throw new Error('Invalid insertion state');

      state.insertingObjectInfo.data = {
        ...state.insertingObjectInfo.data,
        ...action.payload,
        trussEnvelopeGuids: castDraft(
          action.payload.trussEnvelopeGuids ?? state.insertingObjectInfo.data.trussEnvelopeGuids,
        ),
      };
    },

    applicationOnStartPickingTrussEnvelopesForTrussGroup: (
      state,
      action: PayloadAction<{ trussGroupGuid: string | null }>,
    ) => {
      state.inDimensionMode = false;
      state.manualPickingObjectInfo = {
        type: 'pickingTrussEnvelopesForTrussGroup',
        trussGroupGuid: action.payload.trussGroupGuid,
        trussEnvelopeGuidsToAdd: [],
        trussEnvelopeGuidsToRemove: [],
      };
    },

    applicationOnUpdatePickingTrussEnvelopesForTrussGroup: (
      state,
      action: PayloadAction<{
        trussEnvelopeGuidsToAdd: readonly string[];
        trussEnvelopeGuidsToRemove: readonly string[];
      }>,
    ) => {
      if (state.manualPickingObjectInfo?.type !== 'pickingTrussEnvelopesForTrussGroup') {
        throw new Error('Invalid picking state');
      }

      state.inDimensionMode = false;
      state.manualPickingObjectInfo = {
        ...state.manualPickingObjectInfo,
        type: 'pickingTrussEnvelopesForTrussGroup',
        trussEnvelopeGuidsToAdd: castDraft(action.payload.trussEnvelopeGuidsToAdd),
        trussEnvelopeGuidsToRemove: castDraft(action.payload.trussEnvelopeGuidsToRemove),
      };
    },

    applicationOnInsertingCornerSet: (state, action: PayloadAction<{ girderGuid: string }>) => {
      state.insertingObjectInfo = {
        type: 'cornerSet',
        data: { type: 'Stepdown', girderGuid: action.payload.girderGuid, spacing: 24, minJackLength: 3 },
      };
    },

    setInsertionCornerSetData: (state, action: PayloadAction<Partial<InsertingCornerSetInfo['data']>>) => {
      if (state.insertingObjectInfo?.type !== 'cornerSet') throw new Error('Invalid insertion state');

      state.insertingObjectInfo.data = {
        ...state.insertingObjectInfo.data,
        ...action.payload,
      };
    },

    applicationOnInsertingWorkLine: (state, action: PayloadAction<{ scope: WorkLineScope }>) => {
      const previousSettings =
        state.insertingObjectInfo?.type === 'workLine' ? state.insertingObjectInfo.settings : undefined;

      state.insertingObjectInfo = {
        type: 'workLine',
        draft: { phase: 'idle' },
        settings: previousSettings ?? {
          bounding: false,
          segmentDesired: false,
          autoInsert: false,
          divisionQuantity: 1,
        },
        scope: action.payload.scope,
        liveGestureLineType: null,
      };
    },

    setWorkLineInsertionScope: (state, action: PayloadAction<WorkLineScope>) => {
      if (state.insertingObjectInfo?.type !== 'workLine') return;

      state.insertingObjectInfo.scope = action.payload;
    },

    setWorkLineInsertionFirstSelection: (state, action: PayloadAction<WorkLineInsertionSelection>) => {
      if (state.insertingObjectInfo?.type !== 'workLine') throw new Error('Invalid state');

      state.insertingObjectInfo.draft = { phase: 'gesturing', firstSelection: castDraft(action.payload) };
    },

    setWorkLineInsertionSettings: (state, action: PayloadAction<Partial<WorkLineInsertionSettings>>) => {
      if (state.insertingObjectInfo?.type !== 'workLine') return;

      state.insertingObjectInfo.settings = { ...state.insertingObjectInfo.settings, ...action.payload };

      if (action.payload.divisionQuantity != null) {
        state.insertingObjectInfo.settings.divisionQuantity = Math.max(1, action.payload.divisionQuantity);
      }

      const { draft } = state.insertingObjectInfo;
      if (
        action.payload.segmentDesired != null &&
        draft.phase === 'reviewing' &&
        draft.line.type === 'point-to-point'
      ) {
        draft.line.segment = action.payload.segmentDesired;
      }

      if (action.payload.divisionQuantity != null && draft.phase === 'reviewing' && draft.line.type === 'division') {
        draft.line.quantity = state.insertingObjectInfo.settings.divisionQuantity;
      }
    },

    resetWorkLineGesture: (state) => {
      if (state.insertingObjectInfo?.type !== 'workLine') return;

      state.insertingObjectInfo.draft = { phase: 'idle' };
      state.insertingObjectInfo.liveGestureLineType = null;
      state.manualPickingObjectInfo = null;
    },

    __UNSAFE_setWorkLineGestureLineType: (state, action: PayloadAction<InsertionWorkLine['type'] | null>) => {
      if (state.insertingObjectInfo?.type !== 'workLine') return;

      state.insertingObjectInfo.liveGestureLineType = action.payload;
    },

    setInsertionWorkLine: (state, action: PayloadAction<InsertionWorkLine>) => {
      if (state.insertingObjectInfo?.type !== 'workLine') return;

      state.insertingObjectInfo.draft = { phase: 'reviewing', line: castDraft(action.payload) };
      state.insertingObjectInfo.liveGestureLineType = null;
    },

    setInsertionWorkLineTypeManually: (state, action: PayloadAction<InsertionWorkLine['type']>) => {
      if (state.insertingObjectInfo?.type !== 'workLine') return;

      const line = getDefaultInsertionWorkLine(action.payload, {
        segment: state.insertingObjectInfo.settings.segmentDesired,
        divisionQuantity: state.insertingObjectInfo.settings.divisionQuantity,
      });

      state.insertingObjectInfo.draft = { phase: 'reviewing', line: castDraft(line) };
      state.manualPickingObjectInfo = null;
    },

    applicationOnInsertingBearingEnvelope: (
      state,
      action: PayloadAction<{ insertionData: InsertionBearingEnvelope }>,
    ) => {
      state.insertingObjectInfo = {
        type: 'bearingEnvelope',
        data: action.payload.insertionData,
      };
    },

    updateInsertingBearingEnvelope: (
      state,
      action: PayloadAction<{
        bearingType: BearingEnvelopeType;
        changes: Partial<Omit<BearingEnvelope, 'guid' | 'name' | 'bearingType'>>;
      }>,
    ) => {
      if (state.insertingObjectInfo?.type === 'bearingEnvelope') {
        Object.assign(state.insertingObjectInfo.data, action.payload.changes);
      }
    },

    applicationOnInsertingRoofPlane: (state, action: PayloadAction<Partial<NonGeometricInsertionRoofPlane>>) => {
      onInsertingRoofPlane(state, action.payload);
    },

    updateInsertingRoofPlane: (state, action: PayloadAction<Partial<InsertionRoofPlane>>) => {
      if (state.insertingObjectInfo?.type === 'roofPlane') {
        Object.assign(state.insertingObjectInfo.data, action.payload);
      }
    },

    applicationOnInsertingCeilingPlane: (state, action: PayloadAction<Partial<NonGeometricInsertionCeilingPlane>>) => {
      onInsertingCeilingPlane(state, action.payload);
    },

    updateInsertingCeilingPlane: (state, action: PayloadAction<Partial<InsertionCeilingPlane>>) => {
      if (state.insertingObjectInfo?.type === 'ceilingPlane') {
        Object.assign(state.insertingObjectInfo.data, action.payload);
      }
    },

    applicationOnInsertingCeilingTray: (state, action: PayloadAction<Partial<Omit<CeilingTray, 'guid'>>>) => {
      state.insertingObjectInfo = {
        type: 'ceilingTray',
        data: { ...INITIAL_CEILING_TRAY_PROPERTIES, ...action.payload },
      };
    },

    updateInsertingCeilingTray: (state, action: PayloadAction<Partial<Omit<CeilingTray, 'guid'>>>) => {
      if (state.insertingObjectInfo?.type === 'ceilingTray') {
        Object.assign(state.insertingObjectInfo.data, action.payload);
      }
    },

    applicationOnInsertingFeaturePatternContainer: (state, action: PayloadAction<InsertionFeaturePatternContainer>) => {
      state.insertingObjectInfo = {
        type: 'featurePatternContainer',
        data: castDraft(action.payload),
      };
    },

    applicationOnManuallyPickingInsertionFeaturePatternContainerBoundary: (state) => {
      state.manualPickingObjectInfo = {
        type: 'pickingLayoutLine',
        target: 'feature-pattern-container-boundary',
      };
    },

    updateInsertionFeaturePatternContainer: (
      state,
      action: PayloadAction<Partial<InsertionFeaturePatternContainer>>,
    ) => {
      if (state.insertingObjectInfo?.type === 'featurePatternContainer') {
        Object.assign(state.insertingObjectInfo.data, action.payload);
      }
    },

    applicationOnInsertingFeatureArea: (state, action: PayloadAction<string>) => {
      state.insertingObjectInfo = {
        type: 'featureArea',
        data: {
          featurePatternContainerGuid: action.payload,
          value: DEFAULT_CHASE_FEATURE,
        },
      };
    },

    switchInsertionFeatureAreaType: (state, action: PayloadAction<LayoutFeatureArea['featureType']>) => {
      if (state.insertingObjectInfo?.type !== 'featureArea') return;

      switch (action.payload) {
        case 'Chase':
          state.insertingObjectInfo.data.value = DEFAULT_CHASE_FEATURE;
          break;

        case 'Step':
          state.insertingObjectInfo.data.value = DEFAULT_STEP_FEATURE;
          break;

        case 'BeamPocket':
          state.insertingObjectInfo.data.value = DEFAULT_BEAM_POCKET_FEATURE;
          break;

        case 'VerticalStack':
          state.insertingObjectInfo.data.value = DEFAULT_VERTICAL_STACK_FEATURE;
          break;

        default:
          assertExhaustiveSwitch(action.payload);
      }
    },

    updateInsertionFeatureAreaContainerGuid(state, action: PayloadAction<string>) {
      if (state.insertingObjectInfo?.type !== 'featureArea') return;
      state.insertingObjectInfo.data.featurePatternContainerGuid = action.payload;
    },

    updateInsertionFeatureArea: (state, action: PayloadAction<InsertionLayoutFeatureArea>) => {
      if (state.insertingObjectInfo?.type !== 'featureArea') return;
      state.insertingObjectInfo.data.value = action.payload;
    },

    applicationStoreOrganizationAndCustomerGuid: (
      state,
      action: PayloadAction<{ customerGuid: string; organizationGuid: string }>,
    ) => {
      state.customerGuid = action.payload.customerGuid;
      state.organizationGuid = action.payload.organizationGuid;
    },
    applicationClearOrganizationAndCustomerGuid: (state) => {
      state.customerGuid = undefined;
      state.organizationGuid = undefined;
    },
    applicationOrganizationSettingsLoaded: (state, action: PayloadAction<OrganizationSettings | null>) => {
      state.organizationSettings = action.payload;
      state.organizationSettingsLoaded = true;
    },

    applicationOnPickingMemberCutLineGuid: (
      state,
      action: PayloadAction<{
        relativeGeometryGuid: string;
        isForBaseCut: boolean;
        replacingLineGuid: string | undefined;
      }>,
    ) => {
      state.inDimensionMode = false;
      state.manualPickingObjectInfo = {
        type: 'pickingReferenceLine',
        targetInfo: {
          type: 'memberCut',
          replacingLineGuid: action.payload.replacingLineGuid,
          isForBaseCut: action.payload.isForBaseCut,
          relativeGeometryGuid: action.payload.relativeGeometryGuid,
        },
      };
    },
    applicationOnPickingReferenceLine: (state, action: PayloadAction<{ rootGuid: string; lineGuidKey: string }>) => {
      state.inDimensionMode = false;
      state.manualPickingObjectInfo = {
        type: 'pickingReferenceLine',
        targetInfo: { type: 'referenceLine', rootGuid: action.payload.rootGuid, key: action.payload.lineGuidKey },
      };
    },
    applicationOnPickingMemberLine: (state, action: PayloadAction<string>) => {
      state.inDimensionMode = false;
      state.manualPickingObjectInfo = {
        type: 'pickingReferenceLine',
        targetInfo: { type: 'memberLine', memberGuid: action.payload },
      };
    },
    applicationOnPickingRegularBearingLine: (
      state,
      action: PayloadAction<{
        bearingGeometryType: 'Bearing';
        propertyName: 'bearingLineGuid' | 'lineGuid';
        bearingGuid: string;
      }>,
    ) => {
      state.inDimensionMode = false;
      state.manualPickingObjectInfo = {
        type: 'pickingReferenceLine',
        targetInfo: { type: 'bearingLine', ...action.payload },
      };
    },
    applicationOnPickingContinuousBearingLine: (
      state,
      action: PayloadAction<{
        bearingGeometryType: 'ContinuousBearing';
        propertyName: 'bearingLineGuid' | 'leftSideLineGuid' | 'rightSideLineGuid';
        bearingGuid: string;
      }>,
    ) => {
      state.inDimensionMode = false;
      state.manualPickingObjectInfo = {
        type: 'pickingReferenceLine',
        targetInfo: { type: 'bearingLine', ...action.payload },
      };
    },
    applicationOnPickingReferenceLineIntersection: (
      state,
      action: PayloadAction<{
        rootGuid: string;
        lineGuidKey1: keyof any;
        lineGuidKey2: keyof any;
      }>,
    ) => {
      state.inDimensionMode = false;
      state.manualPickingObjectInfo = {
        type: 'pickingReferenceLineIntersection',
        referenceLineInfo: {
          rootGuid: action.payload.rootGuid,
          lineGuidKey1: action.payload.lineGuidKey1,
          lineGuidKey2: action.payload.lineGuidKey2,
        },
      };
    },
    applicationOnPickingBraceLocation: (state) => {
      state.manualPickingObjectInfo = {
        type: 'pickingBraceLocation',
      };
    },
    applicationOnPickingGussetLocation: (state) => {
      state.manualPickingObjectInfo = {
        type: 'pickingGussetLocation',
      };
    },
    applicationOnPickingProfileJoint: (state) => {
      state.manualPickingObjectInfo = { type: 'pickingProfileJoint' };
    },
    applicationOnPickingRoofFace: (
      state,
      action: PayloadAction<{
        readonly ordered: boolean;
        readonly cutOrientation: 'top' | 'bottom';
        readonly targetInfo: TrussEnvelopeTargetInfo;
      }>,
    ) => {
      state.manualPickingObjectInfo = {
        type: 'pickingRoofFace',
        ordered: action.payload.ordered,
        cutOrientation: action.payload.cutOrientation,
        targetInfo: action.payload.targetInfo,
      };
    },
    applicationOnPickingPlane: (state, action: PayloadAction<PickingPlaneInfo['targetInfo']>) => {
      state.manualPickingObjectInfo = {
        ...INITIAL_PLANE_PICKING_INFO,
        targetInfo: action.payload,
      };
    },
    modeSetForPlanePicking: (state, action: PayloadAction<PickingPlaneInfo['activeMode']>) => {
      if (state.manualPickingObjectInfo?.type === 'pickingPlane') {
        state.manualPickingObjectInfo.activeMode = action.payload;
      }
    },
    hoveredPlaneSetDuringPlanePicking: (state: Draft<Application>, action: PayloadAction<HoveredPlaneDataWithType>) => {
      if (state.manualPickingObjectInfo?.type !== 'pickingPlane') return;
      state.manualPickingObjectInfo.hoveredPlane = action.payload;
    },
    hoveredPlaneClearedDuringPlanePicking: (state) => {
      if (state.manualPickingObjectInfo?.type !== 'pickingPlane') return;
      state.manualPickingObjectInfo.hoveredPlane = null;
    },
    applicationOnPickingLayoutLine: (state, action: PayloadAction<Omit<PickingLayoutLine, 'type'>>) => {
      state.manualPickingObjectInfo = { type: 'pickingLayoutLine', ...action.payload };
    },
    applicationOnPickingLayoutPoint: (state, action: PayloadAction<{ target: LayoutPointPickingTarget }>) => {
      state.manualPickingObjectInfo = { type: 'pickingLayoutPoint', target: action.payload.target };

      if (state.insertingObjectInfo?.type === 'bearingEnvelope') {
        if (action.payload.target === 'bearing-envelope-left') {
          state.insertingObjectInfo.data.leftPoint = undefined;
        } else if (action.payload.target === 'bearing-envelope-right') {
          state.insertingObjectInfo.data.rightPoint = undefined;
        }
      }
    },
    addCutOnInsertingTrussEnvelope: (
      state,
      action: PayloadAction<{ readonly cut: TopOrBottomCut; readonly cutOrientation: 'top' | 'bottom' }>,
    ) => {
      if (state.insertingObjectInfo?.type !== 'trussEnvelope') throw new Error('Invalid insertion state.');

      const { cut, cutOrientation } = action.payload;

      if (cutOrientation === 'top') {
        state.insertingObjectInfo.data.topCuts.push(cut);
      } else if (cutOrientation === 'bottom') {
        state.insertingObjectInfo.data.bottomCuts.push(cut);
      } else {
        assertExhaustiveSwitch(cutOrientation);
      }
    },
    applicationOnInsertingPlatePair: (
      state,
      action: PayloadAction<{ initialPosition?: Point2D; initialPlateSize?: PlateOption }>,
    ) => {
      state.insertingObjectInfo = {
        type: 'platePair',

        position: action.payload.initialPosition,
        plateType: action.payload.initialPlateSize?.plateType,
        tangiblePlateTypesForComboPlateType: castDraft(
          action.payload.initialPlateSize?.tangiblePlateTypesForComboPlateType,
        ),
        plateDimensions: action.payload.initialPlateSize
          ? {
              width: action.payload.initialPlateSize.width,
              length: action.payload.initialPlateSize.length,
              geometryType: action.payload.initialPlateSize.geometryType,
            }
          : undefined,
        platePlacement: 'FrontBack',
      };
    },
    clearInsertingObject: (state) => {
      state.insertingObjectInfo = null;
      state.manualPickingObjectInfo = null;
    },
    clearPickingObject: (state) => {
      state.manualPickingObjectInfo = null;
    },
    __UNSAFE_pickedPointMovementPending: (state, action: PayloadAction<PendingPickedPointMovement>) => {
      state.pendingPickedPointMovement = action.payload;
    },
    pickedPointMovementCleared: (state) => {
      state.pendingPickedPointMovement = null;
    },
    updateInsertionPlatePair: (state, action: PayloadAction<Partial<InsertingPlatePairInfo>>) => {
      const { insertingObjectInfo } = state;

      if (insertingObjectInfo?.type === 'platePair') {
        state.insertingObjectInfo = castDraft({
          ...insertingObjectInfo,
          ...action.payload,
          type: 'platePair', // ensure the type can't be changed by the payload
        });
      }
    },
    setLastMemberLineSelectionPoint: (state, action: PayloadAction<Point2D>) => {
      state.lastMemberLineSelectionPoint = action.payload;
    },
    startInsertingComponentDesign: (
      state,
      action: PayloadAction<{
        parentGuid: string | null;
        previouslySelectedComponentDesignGuid: string | null;

        componentType: ComponentType;
        definitionType: ComponentDefinitionType;
        templateType: ComponentTemplateType;
        shape: ComponentInsertionShape;

        shapeDetails: ConfigurableShapeDetails | null;

        designSettings: FeaturePatternTrussSettings | ProfileTrussSettings;
        profileDefaults: ProfileDefaults | null;
        materialSelection: MaterialSelection;

        loadSettings: LoadSettings;
        analysisSettings: AnalysisSettings;
        materialSettings: MaterialSettingsPair;
      }>,
    ) => {
      state.insertingObjectInfo = {
        type: 'componentDesign',
        parentGuid: action.payload.parentGuid,
        oldComponentDesign: null,
        previouslySelectedComponentDesignGuid: action.payload.previouslySelectedComponentDesignGuid,
        loadedInfo: { isLoaded: false },
        loadingInfo: { isLoading: false },
        componentType: action.payload.componentType,
        definitionType: action.payload.definitionType,
        templateType: action.payload.templateType,
        shape: action.payload.shape,
        stage: action.payload.definitionType === 'Profile' ? 'ChoosingShape' : 'ShapeDetails',

        shapeDetails: action.payload.shapeDetails,

        designSettings: castDraft(action.payload.designSettings),
        profileDefaults: action.payload.profileDefaults,
        materialSelection: action.payload.materialSelection,
        loadSettings: castDraft(action.payload.loadSettings),
        analysisSettings: action.payload.analysisSettings,
        materialSettings: castDraft(action.payload.materialSettings),

        loadSettingsState: 'initial',
        analysisSettingsState: 'initial',
        materialSettingsState: 'initial',

        jackConnections: [],
        jackInformation: {},
      };
    },
    switchInsertionComponentDesignType: (
      state,
      action: PayloadAction<{
        componentType: ComponentType;
        definitionType: ComponentDefinitionType;
        templateType: ComponentTemplateType;
        shape: ComponentInsertionShape;

        shapeDetails: ConfigurableShapeDetails | null;

        designSettings: FeaturePatternTrussSettings | ProfileTrussSettings;
      }>,
    ) => {
      const { insertingObjectInfo } = state;
      if (!insertingObjectInfo || insertingObjectInfo.type !== 'componentDesign') {
        console.error('Unable to update component design insertion when not component design insertion is not active.');
        return;
      }

      const hadAtticSpace = designSettingsHaveAtticSpace(insertingObjectInfo.designSettings);

      insertingObjectInfo.componentType = action.payload.componentType;
      insertingObjectInfo.definitionType = action.payload.definitionType;
      insertingObjectInfo.templateType = action.payload.templateType;
      insertingObjectInfo.shape = action.payload.shape;
      insertingObjectInfo.shapeDetails = action.payload.shapeDetails;
      insertingObjectInfo.designSettings = castDraft(action.payload.designSettings);

      synchronizeAtticLoadWithAtticSpace(insertingObjectInfo, hadAtticSpace);

      insertingObjectInfo.loadedInfo = { isLoaded: false };
      insertingObjectInfo.loadingInfo = { isLoading: false };
    },
    setInsertionComponentStage: (state, action: PayloadAction<ComponentInsertionStage>) => {
      const { insertingObjectInfo } = state;
      if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Component insertion is not active');

      insertingObjectInfo.stage = action.payload;
    },
    setInsertionComponentType: (state, action: PayloadAction<{ componentType: ComponentType; name: string }>) => {
      const { insertingObjectInfo } = state;
      if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Component insertion is not active');

      insertingObjectInfo.componentType = action.payload.componentType;
      insertingObjectInfo.designSettings.componentType = action.payload.componentType;
      insertingObjectInfo.designSettings.name = action.payload.name;
    },
    updateShapeDetails: (
      state,
      action: PayloadAction<{ shapeDetails: ConfigurableShapeDetails; profileSettings: ProfileTrussSettings }>,
    ) => {
      const { insertingObjectInfo } = state;
      if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Component insertion is not active');

      const { shapeDetails, profileSettings } = action.payload;

      if (
        insertingObjectInfo.shapeDetails?.type !== shapeDetails.type ||
        insertingObjectInfo.designSettings?.definitionType !== profileSettings.definitionType
      ) {
        throw new Error('Expected shape type and definition type to match');
      }

      const hadAtticSpace = designSettingsHaveAtticSpace(insertingObjectInfo.designSettings);

      insertingObjectInfo.shapeDetails = shapeDetails;
      insertingObjectInfo.designSettings = castDraft(profileSettings);

      synchronizeAtticLoadWithAtticSpace(insertingObjectInfo, hadAtticSpace);
    },
    updateProfileInsertionWebbingInfo: (state, action: PayloadAction<WebbingInfo>) => {
      const { insertingObjectInfo } = state;
      if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Component insertion is not active');

      insertingObjectInfo.webbingInfo = castDraft(action.payload);
    },
    applicationOnRegeneratingComponentDesign: (
      state,
      action: PayloadAction<{
        parentGuid: string | null;
        componentType: ComponentType;
        definitionType: ComponentDefinitionType;
        shape: ComponentInsertionShape;
        stage: ComponentInsertionStage;
        oldComponentDesign: ComponentDesign | null;

        designSettings: FeaturePatternTrussSettings | ProfileTrussSettings;
        profileDefaults: ProfileDefaults | null;
        materialSelection: MaterialSelection;
        loadSettings: LoadSettings;
        analysisSettings: AnalysisSettings;
        materialSettings: MaterialSettingsPair;

        jackConnections: readonly JackGirderConnection[];
        jackInformation: { readonly [componentDesignGuid: string]: JackInformation };
      }>,
    ) => {
      if (state.insertingObjectInfo && state.insertingObjectInfo.type === 'componentDesign') {
        console.assert(false);
      } else {
        state.insertingObjectInfo = {
          type: 'componentDesign',
          parentGuid: action.payload.parentGuid,
          oldComponentDesign: castDraft(action.payload.oldComponentDesign),
          previouslySelectedComponentDesignGuid: null,
          loadedInfo: { isLoaded: false },
          loadingInfo: { isLoading: false },
          componentType: action.payload.componentType,
          definitionType: action.payload.definitionType,
          templateType: 'default',
          shape: action.payload.shape,
          stage: action.payload.stage,

          shapeDetails: null,

          designSettings: castDraft(action.payload.designSettings),
          profileDefaults: action.payload.profileDefaults,
          materialSelection: action.payload.materialSelection,
          loadSettings: castDraft(action.payload.loadSettings),
          analysisSettings: action.payload.analysisSettings,
          materialSettings: castDraft(action.payload.materialSettings),

          // We set these as modified so that they get saved regardless of whether they've actually been modified.
          // There maybe a better way to ensure that the new component design gets the same settings as the old
          // component design, but this seemed like the best option for now
          loadSettingsState: 'modified',
          analysisSettingsState: 'modified',
          materialSettingsState: 'modified',

          jackConnections: castDraft(action.payload.jackConnections),
          jackInformation: castDraft(action.payload.jackInformation),
        };
      }
    },
    startInsertionComponentDownload: (state, action: PayloadAction<{}>) => {
      const { insertingObjectInfo } = state;
      if (insertingObjectInfo) {
        if (insertingObjectInfo.type === 'componentDesign') {
          insertingObjectInfo.loadingInfo = { isLoading: true, downloadId: action.payload };
        } else {
          console.assert(false);
        }
      } else {
        console.assert(false);
      }
    },
    finishInsertionComponentDownload: (state, action: PayloadAction<string>) => {
      const { insertingObjectInfo } = state;
      if (insertingObjectInfo) {
        if (insertingObjectInfo.type === 'componentDesign') {
          insertingObjectInfo.loadingInfo = { isLoading: false };
          insertingObjectInfo.loadedInfo = { isLoaded: true, guid: action.payload };
        } else {
          console.assert(false);
        }
      } else {
        console.assert(false);
      }
    },
    setComponentInsertionMemberAnalysisInfo: (state, action: PayloadAction<MemberAnalysisInfo | null>) => {
      const { insertingObjectInfo } = state;
      if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Expected to be inserting a component.');
      insertingObjectInfo.memberAnalysisInfo = castDraft(action.payload);
    },
    startInsertBearingComponentInsertion: (state) => {
      const { insertingObjectInfo } = state;
      if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Expected to be inserting a component.');
      insertingObjectInfo.insertingBearing = true;
    },
    clearInsertBearingComponentInsertion: (state) => {
      const { insertingObjectInfo } = state;
      if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Expected to be inserting a component.');
      insertingObjectInfo.insertingBearing = false;
    },
    setComponentDesignSortByType: (state, action: PayloadAction<ComponentSortType>) => {
      state.componentDesignSortBy = action.payload;
    },
    setComponentOrder: (state, action: PayloadAction<string[]>) => {
      state.componentDesignSortBy = action.payload;
    },
    setComponentListFilters: (state, action: PayloadAction<Partial<ComponentListFilters>>) => {
      state.componentListFilters = { ...state.componentListFilters, ...action.payload };
    },
    resetComponentListFilters: (state) => {
      state.componentListFilters = DEFAULT_COMPONENT_LIST_FILTERS;
    },
    // This is a separate action from `setInsertionFeaturePatternTrussSettings` to make sure that all changes to a
    // profile are run through `updateInsertingProfileTrussSettings`.
    setInsertionProfileTrussSettings: (state, action: PayloadAction<ProfileTrussSettings>) => {
      if (state.insertingObjectInfo && state.insertingObjectInfo.type === 'componentDesign') {
        const { insertingObjectInfo } = state;
        const hadAtticSpace = designSettingsHaveAtticSpace(insertingObjectInfo.designSettings);

        insertingObjectInfo.designSettings = castDraft(action.payload);

        synchronizeAtticLoadWithAtticSpace(insertingObjectInfo, hadAtticSpace);
      } else {
        throw new Error();
      }
    },
    setInsertionFeaturePatternTrussSettings: (state, action: PayloadAction<FeaturePatternTrussSettings>) => {
      if (state.insertingObjectInfo && state.insertingObjectInfo.type === 'componentDesign') {
        state.insertingObjectInfo.designSettings = castDraft(action.payload);
      } else {
        throw new Error();
      }
    },
    setInsertionLoadSettings: (state, action: PayloadAction<{ settings: LoadSettings; state: SettingsState }>) => {
      if (state.insertingObjectInfo && state.insertingObjectInfo.type === 'componentDesign') {
        state.insertingObjectInfo.loadSettings = castDraft(action.payload.settings);
        state.insertingObjectInfo.loadSettingsState = action.payload.state;
      } else {
        throw new Error();
      }
    },
    setInsertionAnalysisSettings: (
      state,
      action: PayloadAction<{ settings: AnalysisSettings; state: SettingsState }>,
    ) => {
      if (state.insertingObjectInfo && state.insertingObjectInfo.type === 'componentDesign') {
        state.insertingObjectInfo.analysisSettings = castDraft(action.payload.settings);
        state.insertingObjectInfo.analysisSettingsState = action.payload.state;
      } else {
        throw new Error();
      }
    },
    setInsertionDesignStandard: (state, action: PayloadAction<{ designStandard: DesignStandard }>) => {
      if (state.insertingObjectInfo && state.insertingObjectInfo.type === 'componentDesign') {
        state.insertingObjectInfo.analysisSettings.designStandard = action.payload.designStandard;
        state.insertingObjectInfo.analysisSettingsState = 'modified';
      } else {
        throw new Error();
      }
    },
    setInsertionMaterialSettings: (
      state,
      action: PayloadAction<{ settings: MaterialSettingsPair; state: SettingsState }>,
    ) => {
      if (state.insertingObjectInfo && state.insertingObjectInfo.type === 'componentDesign') {
        state.insertingObjectInfo.materialSettings = castDraft(action.payload.settings);
        state.insertingObjectInfo.materialSettingsState = action.payload.state;
      } else {
        throw new Error();
      }
    },
    setInsertionMaterialSelection: (state, action: PayloadAction<MaterialSelection>) => {
      if (state.insertingObjectInfo && state.insertingObjectInfo.type === 'componentDesign') {
        state.insertingObjectInfo.materialSelection = action.payload;
      } else {
        throw new Error();
      }
    },
    setDetailedAnalysis: (state, action: PayloadAction<boolean>) => {
      state.detailedAnalysis = action.payload;
    },
    setRegenerateTddsAutomatically: (state, action: PayloadAction<boolean>) => {
      state.regenerateTddsAutomatically = action.payload;
    },
    setRunPlateMislocations: (state, action: PayloadAction<boolean>) => {
      state.runPlateMislocations = action.payload;
    },
    setRunExhaustivePlateSearch: (state, action: PayloadAction<boolean>) => {
      state.runExhaustivePlateSearch = action.payload;
    },
    setRunOptimizations: (state, action: PayloadAction<boolean>) => {
      state.runOptimizations = action.payload;
    },
    setAllowOptimizationChordResizing: (state, action: PayloadAction<boolean>) => {
      state.allowOptimizationChordResizing = action.payload;
    },
    setBasicInsertionFirstSelection: (state, action: PayloadAction<BasicInsertionFirstSelection>) => {
      if (state.insertingObjectInfo?.type !== 'member' && state.insertingObjectInfo?.type !== 'referenceLine') {
        throw new Error('Invalid state');
      }

      state.insertingObjectInfo.basicDefinition.firstSelection = action.payload;
    },
    setBasicInsertionPlurality: (state, action: PayloadAction<number>) => {
      if (state.insertingObjectInfo?.type !== 'member' && state.insertingObjectInfo?.type !== 'referenceLine') {
        throw new Error('Invalid state');
      }

      state.insertingObjectInfo.basicDefinition.settings.plurality = action.payload;
    },
    startImportComponent: (state, action: PayloadAction<{ parentGuid?: string }>) => {
      state.importingComponentInfo = {
        parentGuid: action.payload.parentGuid,
      };
    },
    setImportTemplateComponent: (state, action: PayloadAction<{ componentDesignGuid: string }>) => {
      if (!state.importingComponentInfo) {
        state.importingComponentInfo = { templateComponentGuid: action.payload.componentDesignGuid };
      } else {
        state.importingComponentInfo.templateComponentGuid = action.payload.componentDesignGuid;
      }
    },
    closeImportComponentPanel: (state) => {
      state.importingComponentInfo = null;
    },
    startRewebbing: (
      state,
      action: PayloadAction<{
        profileSettings: RewebbingProfileSettings;
        materialSettings: MaterialSettings;
        loadSettings: LoadSettings;
        analysisSettings: AnalysisSettings;
        jackConnections: readonly JackGirderConnection[];
        jackInformation: { readonly [componentDesignGuid: string]: JackInformation };
      }>,
    ) => {
      const { profileSettings, materialSettings, loadSettings, analysisSettings, jackConnections, jackInformation } =
        action.payload;
      state.rewebbingInfo = {
        profileSettings: castDraft(profileSettings),
        webbingInfo: null,
        memberAnalysisInfo: null,

        materialSettings: castDraft(materialSettings),
        loadSettings: castDraft(loadSettings),
        analysisSettings,
        jackConnections: castDraft(jackConnections),
        jackInformation: castDraft(jackInformation),
      };
    },
    setRewebbingProfileSettings: (state, action: PayloadAction<RewebbingProfileSettings>) => {
      if (!state.rewebbingInfo) throw new Error('Expected rewebbing info to be set.');
      state.rewebbingInfo.profileSettings = castDraft(action.payload);
    },
    clearRewebbingInfo: (state) => {
      state.rewebbingInfo = null;
    },
    setWebbingInfo: (state, action: PayloadAction<WebbingInfo>) => {
      if (!state.rewebbingInfo) throw new Error('Expected rewebbing info to be set.');
      state.rewebbingInfo.webbingInfo = castDraft(action.payload);
    },
    setMemberAnalysisInfo: (state, action: PayloadAction<MemberAnalysisInfo | null>) => {
      if (!state.rewebbingInfo) throw new Error('Expected rewebbing info to be set.');
      state.rewebbingInfo.memberAnalysisInfo = castDraft(action.payload);
    },
    deleteWebSegmentsRewebbing: (state, action: PayloadAction<{ readonly webSegmentGuids: readonly string[] }>) => {
      if (!state.rewebbingInfo?.webbingInfo) throw new Error('Expected rewebbing info to be set.');
      deleteWebSegments(state.rewebbingInfo.webbingInfo, action.payload.webSegmentGuids);
    },
    createWebSegmentRewebbing: (
      state,
      action: PayloadAction<{ webbingRegionIndex: number; baseJointGuid: string; endJointGuid: string }>,
    ) => {
      if (!state.rewebbingInfo?.webbingInfo) throw new Error('Expected rewebbing info to be set.');
      const { webbingRegionIndex, baseJointGuid, endJointGuid } = action.payload;
      createWebSegment(state.rewebbingInfo.webbingInfo, webbingRegionIndex, baseJointGuid, endJointGuid);
    },
    deleteWebSegmentsComponentInsertion: (
      state,
      action: PayloadAction<{ readonly webSegmentGuids: readonly string[] }>,
    ) => {
      if (state.insertingObjectInfo?.type !== 'componentDesign' || !state.insertingObjectInfo.webbingInfo) {
        throw new Error('Expected to be inserting a component with webbing info.');
      }
      deleteWebSegments(state.insertingObjectInfo.webbingInfo, action.payload.webSegmentGuids);
    },
    createWebSegmentComponentInsertion: (
      state,
      action: PayloadAction<{ webbingRegionIndex: number; baseJointGuid: string; endJointGuid: string }>,
    ) => {
      if (state.insertingObjectInfo?.type !== 'componentDesign' || !state.insertingObjectInfo.webbingInfo) {
        throw new Error('Expected to be inserting a component with webbing info.');
      }
      const { webbingRegionIndex, baseJointGuid, endJointGuid } = action.payload;
      createWebSegment(state.insertingObjectInfo.webbingInfo, webbingRegionIndex, baseJointGuid, endJointGuid);
    },
    setSnappingLevel: (state, action: PayloadAction<SnappingLevel>) => {
      state.snappingLevel = action.payload;
    },
    transformationBeganOrUpdated: (state, action: PayloadAction<IPartialTransformation>) => {
      state.transformingObjectsInfo = action.payload;
    },
    transformationDuplicationToggled: (state) => {
      if (state.transformingObjectsInfo) {
        state.transformingObjectsInfo.duplicate = !state.transformingObjectsInfo.duplicate;
      }
    },
    transformationLevelReassignmentEnabled: (state, action: PayloadAction<{ levelGuid: string }>) => {
      if (state.transformingObjectsInfo) {
        state.transformingObjectsInfo.reassign = { levelGuid: action.payload.levelGuid };
      }
    },
    transformationLevelReassignmentDisabled: (state) => {
      if (state.transformingObjectsInfo) {
        state.transformingObjectsInfo.reassign = false;
      }
    },
    transformationLevelGuidSet: (state, action: PayloadAction<string>) => {
      if (state.transformingObjectsInfo?.reassign) {
        state.transformingObjectsInfo.reassign.levelGuid = action.payload;
      }
    },
    transformationFeaturePatternContainerGuidSet: (state, action: PayloadAction<string | null>) => {
      if (state.transformingObjectsInfo?.reassign) {
        state.transformingObjectsInfo.reassign.featurePatternContainerGuid = action.payload;
      }
    },
    transformationComponentLinksPreservationToggled: (state) => {
      if (state.transformingObjectsInfo) {
        state.transformingObjectsInfo.preserveLinkedComponents =
          !state.transformingObjectsInfo.preserveLinkedComponents;
      }
    },
    transformationCanceledOrEnded: (state) => {
      state.transformingObjectsInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadStation, (state, action) => {
      if (action.payload.setAsActive) {
        state.openContext = {
          type: action.payload.station.followsSchedule ? 'schedule-station' : 'station',
          stationGuid: action.payload.station.guid,
        };
      }
    });

    builder.addCase(commitMembers, (state) => {
      state.insertingObjectInfo = null;
      state.manualPickingObjectInfo = null;
    });

    builder.addCase(commitBearing, (state) => {
      state.insertingObjectInfo = null;
      state.manualPickingObjectInfo = null;
    });

    builder.addCase(layoutSlice.actions.roofPlaneCreated, (state, action) => {
      const { postInsertionAction, roofPlaneInsertionSettings, roofPlane } = action.payload;

      switch (postInsertionAction) {
        case 'repeat':
          onInsertingRoofPlane(state, {
            ...roofPlaneInsertionSettings.defaultProperties,
            levelGuid: roofPlane.levelGuid,
          });
          break;
        case 'finish':
          state.insertingObjectInfo = null;
          state.manualPickingObjectInfo = null;
          break;
        case 'nothing':
          break;
        default:
          assertExhaustiveSwitch(postInsertionAction);
      }
    });

    builder.addCase(layoutSlice.actions.ceilingPlaneCreated, (state, action) => {
      const { postInsertionAction, ceilingPlaneInsertionSettings, ceilingPlane } = action.payload;

      switch (postInsertionAction) {
        case 'repeat':
          onInsertingCeilingPlane(state, {
            ...ceilingPlaneInsertionSettings.defaultProperties,
            levelGuid: ceilingPlane.levelGuid,
          });
          break;

        case 'finish':
          state.insertingObjectInfo = null;
          state.manualPickingObjectInfo = null;
          break;

        default:
          assertExhaustiveSwitch(postInsertionAction);
      }
    });

    builder.addCase(layoutSlice.actions.ceilingTrayCreated, (state) => {
      state.insertingObjectInfo = null;
      state.manualPickingObjectInfo = null;
    });

    builder.addCase(layoutSlice.actions.featurePatternContainerCreated, (state) => {
      state.insertingObjectInfo = null;
      state.manualPickingObjectInfo = null;
    });

    builder.addCase(layoutSlice.actions.featureAreasCreated, (state) => {
      state.insertingObjectInfo = null;
      state.manualPickingObjectInfo = null;
    });

    builder.addCase(setMatchFasciaHeightsPanelOpen, (state) => {
      state.manualPickingObjectInfo = {
        ...INITIAL_PLANE_PICKING_INFO,
        targetInfo: { type: 'fascia-height-reference' },
      };
    });

    for (const action of [
      __UNSAFE_layoutItemsSelected,
      __UNSAFE_designItemsSelected,
      __UNSAFE_itemsDeselected,
      clearSelection,
    ] as const) {
      builder.addCase(action, (state) => {
        state.manualPickingObjectInfo = null;
      });
    }
  },
});

function onInsertingRoofPlane(
  state: Draft<Application>,
  initialPropertiesFromSettings: Partial<NonGeometricInsertionRoofPlane>,
) {
  state.insertingObjectInfo = {
    type: 'roofPlane',

    data: castDraft({
      ...INITIAL_ROOF_PLANE_PROPERTIES,
      ...initialPropertiesFromSettings,
      guid: crypto.randomUUID(),
    }),
  };

  state.manualPickingObjectInfo = {
    type: 'pickingLayoutLine',
    target: 'roof-plane-definition',
  };
}

function onInsertingCeilingPlane(
  state: Draft<Application>,
  initialPropertiesFromSettings: Partial<NonGeometricInsertionCeilingPlane>,
) {
  state.insertingObjectInfo = {
    type: 'ceilingPlane',

    data: castDraft({
      ...INITIAL_CEILING_PLANE_PROPERTIES,
      ...initialPropertiesFromSettings,
      guid: crypto.randomUUID(),
    }),
  };

  state.manualPickingObjectInfo = {
    type: 'pickingLayoutLine',
    target: 'ceiling-plane-definition',
  };
}

function deleteWebSegments(webbingInfo: Draft<WebbingInfo>, webSegmentGuids: readonly string[]) {
  for (const webbingRegion of webbingInfo.webbingRegions) {
    webbingRegion.webSegments = webbingRegion.webSegments.filter(
      (webSegment) => !webSegmentGuids.includes(webSegment.guid),
    );
  }
}

function createWebSegment(
  webbingInfo: Draft<WebbingInfo>,
  webbingRegionIndex: number,
  baseJointGuid: string,
  endJointGuid: string,
) {
  const webbingRegion = webbingInfo.webbingRegions[webbingRegionIndex];
  if (
    webbingRegion.calculatedWebJoints.some((joint) => joint.guid === baseJointGuid) &&
    webbingRegion.calculatedWebJoints.some((joint) => joint.guid === endJointGuid)
  ) {
    webbingRegion.webSegments.push({
      guid: crypto.randomUUID(),
      baseJointGuid,
      endJointGuid,
      webType: 'OrdinaryWeb',
      cutByStepKWebGuid: null,
    });
  }
}

export const {
  startup,
  setDimensionMode,
  toggleMemberCuttingMode,
  toggleDimensionMode,
  startInsertMember,
  applicationOnStartDropTopChords,
  editDropTopChordsInsertion,
  applicationOnInsertingMemberStack,
  applicationStartInsertBearing,
  applicationOnInsertingSplice,
  applicationOnInsertingManualLoad,
  applicationOnInsertingJackGirderConnection,
  applicationOnInsertingMovingLoadSettings,
  applicationOnInsertingParapetLoadSettings,
  applicationOnInsertingLoadCase,
  applicationOnInsertingReferenceLine,
  applicationOnInsertingGusset,
  applicationOnSetInsertionGussetCenterPoint,
  applicationStoreOrganizationAndCustomerGuid,
  applicationClearOrganizationAndCustomerGuid,
  applicationOrganizationSettingsLoaded,
  applicationOnPickingMemberCutLineGuid,
  applicationOnPickingReferenceLine,
  applicationOnPickingMemberLine,
  applicationOnPickingRegularBearingLine,
  applicationOnPickingContinuousBearingLine,
  applicationOnPickingReferenceLineIntersection,
  applicationOnPickingBraceLocation,
  applicationOnPickingGussetLocation,
  applicationOnPickingProfileJoint,
  applicationOnPickingRoofFace,
  applicationOnPickingPlane,
  modeSetForPlanePicking,
  hoveredPlaneSetDuringPlanePicking,
  hoveredPlaneClearedDuringPlanePicking,
  applicationOnPickingLayoutLine,
  applicationOnPickingLayoutPoint,
  addCutOnInsertingTrussEnvelope,
  applicationOnInsertingPlatePair,
  applicationOnInsertingTrussEnvelope,
  resetInsertionTrussEnvelope,
  setInsertionTrussEnvelopeProperties,
  applicationOnInsertingTrussEnvelopeRun,
  setInsertionRunSpacing,
  setInsertionRunCalculation,
  applicationOnInsertingTrussGroup,
  updateInsertingTrussGroup,
  applicationOnStartPickingTrussEnvelopesForTrussGroup,
  applicationOnUpdatePickingTrussEnvelopesForTrussGroup,
  applicationOnInsertingCornerSet,
  setInsertionCornerSetData,
  applicationOnInsertingWorkLine,
  setWorkLineInsertionScope,
  setWorkLineInsertionFirstSelection,
  setWorkLineInsertionSettings,
  resetWorkLineGesture,
  __UNSAFE_setWorkLineGestureLineType,
  setInsertionWorkLine,
  setInsertionWorkLineTypeManually,
  applicationOnInsertingBearingEnvelope,
  updateInsertingBearingEnvelope,
  applicationOnInsertingRoofPlane,
  updateInsertingRoofPlane,
  applicationOnInsertingCeilingPlane,
  updateInsertingCeilingPlane,
  applicationOnInsertingCeilingTray,
  updateInsertingCeilingTray,
  applicationOnInsertingFeaturePatternContainer,
  applicationOnManuallyPickingInsertionFeaturePatternContainerBoundary,
  updateInsertionFeaturePatternContainer,
  applicationOnInsertingFeatureArea,
  switchInsertionFeatureAreaType,
  updateInsertionFeatureAreaContainerGuid,
  updateInsertionFeatureArea,
  clearInsertingObject,
  clearPickingObject,
  __UNSAFE_pickedPointMovementPending,
  pickedPointMovementCleared,
  updateInsertionPlatePair,
  setLastMemberLineSelectionPoint,
  startInsertingComponentDesign,
  switchInsertionComponentDesignType,
  setInsertionComponentStage,
  setInsertionComponentType,
  updateShapeDetails,
  updateProfileInsertionWebbingInfo,
  applicationOnRegeneratingComponentDesign,
  startInsertionComponentDownload,
  finishInsertionComponentDownload,
  setComponentInsertionMemberAnalysisInfo,
  startInsertBearingComponentInsertion,
  clearInsertBearingComponentInsertion,
  setComponentDesignSortByType,
  setComponentOrder,
  setComponentListFilters,
  resetComponentListFilters,
  setInsertionProfileTrussSettings,
  setInsertionFeaturePatternTrussSettings,
  setInsertionLoadSettings,
  setInsertionAnalysisSettings,
  setInsertionDesignStandard,
  setInsertionMaterialSettings,
  setInsertionMaterialSelection,
  setDetailedAnalysis,
  setRegenerateTddsAutomatically,
  setRunPlateMislocations,
  setRunExhaustivePlateSearch,
  setRunOptimizations,
  setAllowOptimizationChordResizing,
  setBasicInsertionFirstSelection,
  setBasicInsertionPlurality,
  startImportComponent,
  setImportTemplateComponent,
  closeImportComponentPanel,
  startRewebbing,
  setRewebbingProfileSettings,
  clearRewebbingInfo,
  setWebbingInfo,
  setMemberAnalysisInfo,
  deleteWebSegmentsRewebbing,
  createWebSegmentRewebbing,
  deleteWebSegmentsComponentInsertion,
  createWebSegmentComponentInsertion,
  setSnappingLevel,
  transformationBeganOrUpdated,
  transformationDuplicationToggled,
  transformationLevelReassignmentEnabled,
  transformationLevelReassignmentDisabled,
  transformationLevelGuidSet,
  transformationFeaturePatternContainerGuidSet,
  transformationComponentLinksPreservationToggled,
  transformationCanceledOrEnded,
} = applicationSlice.actions;
export const application = applicationSlice.reducer;
